import { Fragment, useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Inbox, Loader2 } from "lucide-react";

export function useFetch(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await api.get(url, { params: JSON.parse(key) });
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [url, key]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load, setData };
}

export async function act(fn, success) {
  try {
    const r = await fn();
    if (success) toast.success(success);
    return r?.data ?? true;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, tone = "blue", hint, testid }) => {
  const tones = { blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", rose: "bg-rose-50 text-rose-600", slate: "bg-slate-100 text-slate-600" };
  return (
    <div className="panel p-5 transition-colors hover:border-slate-300 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="eyebrow">{label}</span>
        {Icon && <span className={`rounded-md p-2 ${tones[tone]}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <div className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums text-slate-900">{value ?? "-"}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
};

const STATUS_TONE = {
  approved: "emerald", present: "emerald", already_sent: "emerald", completed: "emerald", active: "emerald", open: "blue", hired: "emerald", resolved: "emerald", on_track: "emerald", published: "emerald",
  pending: "amber", pending_manager: "amber", pending_hr: "amber", pending_self: "amber", submitted: "amber", in_progress: "blue", half_day: "amber", screening: "blue", interview: "blue", offered: "violet", exit_interview: "blue", fnf: "violet", at_risk: "amber", draft: "slate",
  rejected: "rose", sent: "emerald", already_sent: "emerald", queued: "blue", failed: "rose", skipped_demo: "slate", not_sent: "slate", cancelled: "slate", archived: "slate", closed: "slate", withdrawn: "slate", absent: "rose", inactive: "slate", exited: "slate", behind: "rose", on_leave: "amber", applied: "slate",
};
const TONE_CLS = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", amber: "bg-amber-50 text-amber-700 border-amber-200", rose: "bg-rose-50 text-rose-700 border-rose-200", blue: "bg-blue-50 text-blue-700 border-blue-200", violet: "bg-violet-50 text-violet-700 border-violet-200", slate: "bg-slate-100 text-slate-600 border-slate-200" };

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap ${TONE_CLS[STATUS_TONE[status] || "slate"]}`}>
    {String(status || "-").replace(/_/g, " ")}
  </span>
);

export const Empty = ({ text = "No records yet" }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400" data-testid="empty-state">
    <Inbox className="h-8 w-8" />
    <span className="text-sm">{text}</span>
  </div>
);

export const Loading = () => (
  <div className="flex justify-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
);

export function DataTable({ columns, rows, loading, testid = "data-table", empty, expandable }) {
  if (loading && !rows) return <Loading />;
  if (!rows?.length) return <Empty text={empty} />;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/80">
            {columns.map((c) => <th key={c.key} className={`px-4 py-2.5 text-left eyebrow whitespace-nowrap ${c.className || ""}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <Fragment key={r.id || i}>
              <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70 transition-colors">
                {columns.map((c) => <td key={c.key} className={`px-4 py-3 text-slate-800 font-medium ${c.className || ""}`}>{c.render ? c.render(r, i) : r[c.key] ?? "-"}</td>)}
              </tr>
              {expandable?.(r, i)?.panel}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const Panel = ({ title, actions, children, className = "", testid }) => (
  <section className={`panel fade-up ${className}`} data-testid={testid}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
        <h3 className="text-sm font-semibold tracking-wide text-slate-700">{title}</h3>
        <div className="flex flex-wrap gap-2">{actions}</div>
      </div>
    )}
    {children}
  </section>
);

export function Field({ f, value, onChange }) {
  const id = `field-${f.name}`;
  const common = { id, "data-testid": `${f.testPrefix || "form"}-${f.name}-input`, required: f.required, value: value ?? "", onChange: (e) => onChange(f.name, e.target.value) };
  let el;
  if (f.type === "select") {
    el = (
      <select className="native-select" {...common}>
        {f.placeholder !== false && <option value="">{f.placeholder || "Select..."}</option>}
        {(f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    );
  } else if (f.type === "textarea") el = <Textarea rows={3} {...common} />;
  else if (f.type === "file") el = <Input type="file" id={id} data-testid={common["data-testid"]} accept={f.accept} required={f.required} onChange={(e) => onChange(f.name, e.target.files?.[0])} />;
  else el = <Input type={f.type || "text"} step={f.step} {...common} />;
  return (
    <div className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-xs font-semibold text-slate-600">{f.label}{f.required && " *"}</Label>
      {el}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Save", testid = "form-dialog" }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVals(initial || {}); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(vals);
    setBusy(false);
    if (ok !== null && ok !== false) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => <Field key={f.name} f={f} value={vals[f.name]} onChange={(k, v) => setVals((s) => ({ ...s, [k]: v }))} />)}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const UserCell = ({ u }) => (
  <div className="flex items-center gap-2.5 min-w-[160px]">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">{(u?.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
    <div className="leading-tight">
      <div className="font-semibold text-slate-900">{u?.name || "-"}</div>
      <div className="text-xs text-slate-500 font-mono">{u?.employee_id}</div>
    </div>
  </div>
);
